/**
 * 时间感知系统 Composable
 * 实现时间上下文生成、对话间隔计算、时段判定
 */

/**
 * 时段类型
 */
export type TimeOfDay = 'late_night' | 'early_morning' | 'morning' | 'afternoon' | 'evening' | 'night'

/**
 * 时间上下文
 */
export interface TimeContext {
  /** 当前时间戳 */
  currentTime: number
  /** 格式化的时间字符串 */
  formattedTime: string
  /** 时段 */
  timeOfDay: TimeOfDay
  /** 时段描述 */
  timeDescription: string
  /** 距离上次对话的间隔（毫秒） */
  timeSinceLastMessage: number
}

/**
 * 时间感知 Composable
 */
export function useTimeAwareness() {
  /**
   * 获取当前时段
   */
  function getTimeOfDay(hour: number): TimeOfDay {
    if (hour >= 0 && hour < 6)
      return 'late_night' // 深夜 0-6点
    if (hour >= 6 && hour < 9)
      return 'early_morning' // 清晨 6-9点
    if (hour >= 9 && hour < 12)
      return 'morning' // 上午 9-12点
    if (hour >= 12 && hour < 18)
      return 'afternoon' // 下午 12-18点
    if (hour >= 18 && hour < 22)
      return 'evening' // 傍晚 18-22点
    return 'night' // 晚上 22-24点
  }

  /**
   * 获取时段描述
   */
  function getTimeDescription(timeOfDay: TimeOfDay): string {
    const descriptions: Record<TimeOfDay, string> = {
      late_night: '深夜',
      early_morning: '清晨',
      morning: '上午',
      afternoon: '下午',
      evening: '傍晚',
      night: '晚上',
    }
    return descriptions[timeOfDay]
  }

  /**
   * 格式化对话间隔
   */
  function formatTimeInterval(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    const weeks = Math.floor(days / 7)
    const months = Math.floor(days / 30)

    if (months > 0)
      return `${months}个月`
    if (weeks > 0)
      return `${weeks}周`
    if (days > 0)
      return `${days}天`
    if (hours > 0)
      return `${hours}小时`
    if (minutes > 0)
      return `${minutes}分钟`
    if (seconds > 10)
      return `${seconds}秒`
    return '刚刚'
  }

  /**
   * 获取完整的时间上下文
   */
  function getTimeContext(lastMessageTime: number = 0): TimeContext {
    const now = new Date()
    const currentTime = now.getTime()

    // 格式化时间
    const formattedTime = now.toLocaleString('zh-CN', {
      dateStyle: 'full',
      timeStyle: 'short',
    })

    // 获取时段信息
    const hour = now.getHours()
    const timeOfDay = getTimeOfDay(hour)
    const timeDescription = getTimeDescription(timeOfDay)

    // 计算对话间隔
    const timeSinceLastMessage = lastMessageTime > 0 ? currentTime - lastMessageTime : 0
    return {
      currentTime,
      formattedTime,
      timeOfDay,
      timeDescription,
      timeSinceLastMessage,
    }
  }

  /**
   * 构建时间感知提示词片段
   */
  function buildTimeAwarenessPrompt(lastMessageTime: number = 0): string {
    const context = getTimeContext(lastMessageTime)
    const interval = context.timeSinceLastMessage > 0
      ? formatTimeInterval(context.timeSinceLastMessage)
      : '首次对话'

    return `# 时间与情景感知 (Time & Context Awareness)\n当前：${context.formattedTime}（${context.timeDescription}）；间隔：${interval}。按时段和间隔自然调整状态、活动与场景。`
  }

  /**
   * 判断是否是长时间间隔（超过6小时）
   */
  function isLongBreak(lastMessageTime: number): boolean {
    if (lastMessageTime === 0)
      return false
    const hours = (Date.now() - lastMessageTime) / (1000 * 60 * 60)
    return hours > 6
  }

  /**
   * 判断是否是跨天对话
   */
  function isCrossDayConversation(lastMessageTime: number): boolean {
    if (lastMessageTime === 0)
      return false
    const lastDate = new Date(lastMessageTime).toDateString()
    const nowDate = new Date().toDateString()
    return lastDate !== nowDate
  }

  /**
   * 获取问候语（根据时段）
   */
  function getGreeting(timeOfDay?: TimeOfDay): string {
    const tod = timeOfDay || getTimeOfDay(new Date().getHours())

    const greetings: Record<TimeOfDay, string[]> = {
      late_night: ['还没睡吗？', '这么晚了', '夜深了'],
      early_morning: ['早安', '早上好', '起来了吗'],
      morning: ['上午好', '早啊', '今天过得怎么样'],
      afternoon: ['下午好', '午安', '中午吃了什么'],
      evening: ['傍晚好', '晚上好', '吃晚饭了吗'],
      night: ['晚上好', '晚安', '准备休息了吗'],
    }

    const options = greetings[tod]
    return options[Math.floor(Math.random() * options.length)]
  }

  return {
    getTimeOfDay,
    getTimeDescription,
    formatTimeInterval,
    getTimeContext,
    buildTimeAwarenessPrompt,
    isLongBreak,
    isCrossDayConversation,
    getGreeting,
  }
}
